import React, { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ExpandToggleIcon } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { COMMON_CLASSES, CARD_SECTION_CLASS, SECTION_TITLE_CLASS } from '@/styles/classes';
import { CallHistoryFilters as Filters } from '@/types/calls';

type FilterSearchActionsProps = {
  onSearch: () => void;
  onClear: () => void;
};

export function FilterSearchActions({ onSearch, onClear }: FilterSearchActionsProps) {
  const { t } = useTranslation();

  return (
    <div className="mt-4 flex gap-3">
      <Button variant="primary" size="md" onClick={onSearch}>
        {t('dataFilters.search')}
      </Button>
      <Button variant="secondary" size="md" onClick={onClear}>
        {t('dataFilters.clear')}
      </Button>
    </div>
  );
}

type SearchStatusFilterFieldsProps = {
  searchLabel: string;
  searchPlaceholder: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  statusValue: boolean | undefined;
  onStatusChange: (value: boolean | undefined) => void;
  gridClassName?: string;
};

export function SearchStatusFilterFields({
  searchLabel,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  statusValue,
  onStatusChange,
  gridClassName = 'grid grid-cols-1 gap-4 md:grid-cols-2',
}: SearchStatusFilterFieldsProps) {
  const { t } = useTranslation();

  return (
    <div className={gridClassName}>
      <Input
        label={searchLabel}
        placeholder={searchPlaceholder}
        value={searchValue}
        onChange={e => onSearchChange(e.target.value)}
      />
      <div>
        <label className={COMMON_CLASSES.label}>{t('users.status')}</label>
        <select
          className={COMMON_CLASSES.filterSelect}
          value={statusValue === undefined ? '' : String(statusValue)}
          onChange={e =>
            onStatusChange(
              e.target.value === '' ? undefined : e.target.value === 'true'
            )
          }
        >
          <option value="">{t('addressBook.allStatus')}</option>
          <option value="true">{t('listFilters.activeOnly')}</option>
          <option value="false">{t('listFilters.inactiveOnly')}</option>
        </select>
      </div>
    </div>
  );
}

type SearchCriteriaPanelProps = {
  children: ReactNode;
  contentClassName?: string;
};

export function SearchCriteriaPanel({
  children,
  contentClassName = 'min-h-[148px] px-5 pb-5',
}: SearchCriteriaPanelProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <section className={CARD_SECTION_CLASS}>
      <div className="flex items-center justify-between px-5 py-[15px]">
        <h2 className={SECTION_TITLE_CLASS}>
          {t('dataFilters.searchCriteria')}
        </h2>
        <button
          type="button"
          aria-expanded={!collapsed}
          aria-label={
            collapsed ? t('dataFilters.expandCriteria') : t('dataFilters.collapseCriteria')
          }
          onClick={() => setCollapsed(current => !current)}
          className="flex size-6 items-center justify-center"
        >
          <ExpandToggleIcon expanded={!collapsed} />
        </button>
      </div>
      {!collapsed ? <div className={contentClassName}>{children}</div> : null}
    </section>
  );
}

interface DataFiltersProps {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  onSearch: () => void;
  onClear: () => void;
}

export const DataFilters: React.FC<DataFiltersProps> = ({
  filters,
  onFiltersChange,
  onSearch,
  onClear,
}) => {
  const { t } = useTranslation();

  const durationPresets = [
    { label: t('dataFilters.custom'), value: 'custom' },
    { label: t('dataFilters.veryShort'), value: 'very-short', min: 0, max: 30 },
    { label: t('dataFilters.short'), value: 'short', min: 30, max: 120 },
    { label: t('dataFilters.medium'), value: 'medium', min: 120, max: 600 },
    { label: t('dataFilters.long'), value: 'long', min: 600, max: 1800 },
    { label: t('dataFilters.veryLong'), value: 'very-long', min: 1800, max: undefined },
  ];

  const hangupCauseOptions = [
    { value: '', label: t('dataFilters.allStatus') },
    { value: 'NORMAL_CLEARING', label: t('dataFilters.completed') },
    { value: 'NO_ANSWER', label: t('dataFilters.noAnswer') },
    { value: 'USER_BUSY', label: t('dataFilters.userBusy') },
    { value: 'CALL_REJECTED', label: t('dataFilters.callRejected') },
  ];

  const handleInputChange = (
    field: keyof Filters,
    value: string | boolean | number | undefined
  ) => {
    onFiltersChange({
      ...filters,
      [field]: value,
      page: 1,
    });
  };

  const handleDurationPresetChange = (presetValue: string) => {
    if (presetValue === 'custom') {
      return;
    }

    const preset = durationPresets.find(entry => entry.value === presetValue);
    if (preset) {
      onFiltersChange({
        ...filters,
        min_duration: preset.min,
        max_duration: preset.max,
        page: 1,
      });
    }
  };

  const getCurrentDurationPreset = () => {
    const preset = durationPresets.find(
      entry => entry.min === filters.min_duration && entry.max === filters.max_duration
    );
    return preset ? preset.value : 'custom';
  };

  return (
    <SearchCriteriaPanel contentClassName="space-y-4 px-5 pb-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Input
          label={t('dataFilters.search')}
          placeholder={t('dataFilters.searchCallsPlaceholder')}
          value={filters.search || ''}
          onChange={e => handleInputChange('search', e.target.value)}
        />
        <Select
          label={t('dataFilters.status')}
          value={filters.hangup_cause || ''}
          onChange={e => handleInputChange('hangup_cause', e.target.value || undefined)}
          options={hangupCauseOptions}
        />
        <Select
          label={t('dataFilters.callType')}
          value={filters.is_video === undefined ? '' : filters.is_video.toString()}
          onChange={e => {
            const value = e.target.value === '' ? undefined : e.target.value === 'true';
            handleInputChange('is_video', value);
          }}
          options={[
            { value: '', label: t('dataFilters.allCalls') },
            { value: 'true', label: t('dataFilters.videoOnly') },
            { value: 'false', label: t('dataFilters.audioOnly') },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label={t('dataFilters.startDate')}
            type="date"
            value={filters.start_date || ''}
            max={filters.end_date || undefined}
            onChange={e => handleInputChange('start_date', e.target.value)}
          />
          <Input
            label={t('dataFilters.endDate')}
            type="date"
            value={filters.end_date || ''}
            min={filters.start_date || undefined}
            onChange={e => handleInputChange('end_date', e.target.value)}
          />
        </div>
        <Select
          label={t('dataFilters.durationPresent')}
          value={getCurrentDurationPreset()}
          onChange={e => handleDurationPresetChange(e.target.value)}
          options={durationPresets.map(preset => ({
            value: preset.value,
            label: preset.label,
          }))}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label={t('dataFilters.minDurationSec')}
            type="number"
            placeholder="0"
            value={filters.min_duration ?? ''}
            min={0}
            onChange={e =>
              handleInputChange(
                'min_duration',
                e.target.value ? parseInt(e.target.value, 10) : undefined
              )
            }
          />
          <Input
            label={t('dataFilters.maxDurationSec')}
            type="number"
            placeholder="0"
            value={filters.max_duration ?? ''}
            min={0}
            onChange={e =>
              handleInputChange(
                'max_duration',
                e.target.value ? parseInt(e.target.value, 10) : undefined
              )
            }
          />
        </div>
      </div>

      <FilterSearchActions onSearch={onSearch} onClear={onClear} />
    </SearchCriteriaPanel>
  );
};
